import GraphXAxisLabelItem from 'components/atoms/GraphXaxisLabelItem';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import {
    barWidth,
    legendData,
    salesGraphWidth,
} from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { height } from 'components/template/GrowthAnalysis/ExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import React, { FC, useContext, useMemo, useRef } from 'react';
import { formatNumWithKMBAlt, getConfig, scaleMaxValue, scaleMinValue } from 'utils/helperMethods';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { ConfigKeys } from 'utils/Enums';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { CustomSelect } from 'CustomComponentCss';
import { DimensionDropdownType } from 'types/pricingSimulator';

interface CategoryMovementChartItemProps {
    chartItem: any;
    idx: number;
    containerWidth: number;
    selectedChartItems: any[];
    setSelectedChartItems: Function;
    loading: boolean;
    error: any;
    currency: string;
    isTopChart?: boolean;
    showLevelDropdown?: boolean;
    listToDisplay: {
        label: string;
        options: DimensionDropdownType[] | null;
    }[];
}

const CategoryMovementChartItem: FC<CategoryMovementChartItemProps> = ({
    chartItem,
    idx,
    containerWidth,
    selectedChartItems,
    setSelectedChartItems,
    loading,
    error,
    currency,
    isTopChart = true,
    showLevelDropdown = false,
    listToDisplay,
}) => {
    const { appConfig } = useContext(UserContext);
    const { selectedReportInfo, updateReportProperty, showPercentageChange } = useContext(PricingAnalyticsDataContext);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const excelHeaders = useMemo(() => {
        return [chartItem?.labelObject?.xAxis, chartItem?.labelObject?.yAxis];
    }, [chartItem]);
    const excelData = useMemo(() => {
        return chartItem?.data?.map((item: any) => [item?.category, item?.sales]);
    }, [chartItem]);

    const chartRef = useRef(null);

    const graphViewContainerWidth = Math.max(
        chartItem?.data ? chartItem.data?.length * salesGraphWidth : 0,
        containerWidth / 2,
    );
    const perGraphComponentWidth = graphViewContainerWidth / chartItem?.data?.length || 0;

    return (
        <div className="p-4 border border-backgroundGreyAlt1 rounded-lg">
            {loading ? (
                <div style={{ height }}>
                    <Loader />
                </div>
            ) : error ? (
                <div style={{ height }}>
                    <ErrorBox msg={String(error)} />
                </div>
            ) : (
                <div ref={chartRef}>
                    <div className="flex items-center gap-1 justify-between">
                        {showLevelDropdown ? (
                            <>
                                <p className="text-subHeaderColor font-sm font-medium">
                                    {chartItem?.labelObject?.mainLabel}
                                </p>
                                <div className="flex gap-2 items-center">
                                    <label className="font-xs">Level</label>
                                    <CustomSelect
                                        className="dropdown-width-multi-select dropdown-height uniqSelPSCompetitionDropdown"
                                        placeholder="Multiple Select"
                                        showArrow
                                        showSearch={false}
                                        value={
                                            selectedReportInfo?.categoryMovement?.input?.[idx]?.bottomChartAttributeType
                                                ?.value
                                        }
                                        onChange={(val: any, item: any) => {
                                            const updatedInput = selectedReportInfo?.categoryMovement?.input?.map(
                                                (graphItem, index) => {
                                                    return {
                                                        ...graphItem,
                                                        bottomChartAttributeType:
                                                            index === idx ? item : graphItem?.bottomChartAttributeType,
                                                    };
                                                },
                                            );
                                            updateReportProperty('categoryMovement', {
                                                output: selectedReportInfo?.categoryMovement?.output,
                                                input: updatedInput,
                                            });
                                        }}
                                        options={listToDisplay}
                                    />
                                    <span className="flex items-center gap-2">
                                        <CopyToClipBoard refOfComponent={chartRef} chartOnly={true} />
                                        <DownloadExcel
                                            title={chartItem?.labelObject?.mainLabel}
                                            excelData={excelData}
                                            headers={excelHeaders}
                                            excludeFilters
                                        />
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-subHeaderColor font-sm font-medium">
                                    {chartItem?.labelObject?.mainLabel}
                                </p>
                                <span className="flex items-center gap-2">
                                    <CopyToClipBoard refOfComponent={chartRef} chartOnly={true} />
                                    <DownloadExcel
                                        title={chartItem?.labelObject?.mainLabel}
                                        excelData={excelData}
                                        headers={excelHeaders}
                                        excludeFilters
                                    />
                                </span>
                            </>
                        )}
                    </div>
                    {/* LEGEND WRAPPER */}
                    <div className="flex justify-between my-4">
                        <p className="font-xs w-20 text-lightBlack">{chartItem?.labelObject?.yAxis}</p>
                        <Legend data={legendData} />
                        <p className="w-3"></p>
                    </div>
                    <div
                        id={`category-movement-${isTopChart ? 'left' : 'right'}-${chartItem.id}`}
                        className="flex ml-1 w-full"
                    >
                        {/* Y AXIS */}
                        <div style={{ minWidth: yAxisGraphWidth }}>
                            <Barchart
                                colorKey="color"
                                dataset={[]}
                                height={height}
                                loading={false}
                                chartContainer={`${isTopChart ? 'left' : 'right'}-SalesY-${chartItem.id}`}
                                fields={['category', 'sales']}
                                minBound={scaleMinValue(80, chartItem?.minVal)}
                                maxBound={scaleMaxValue(80, chartItem?.maxVal)}
                                width={yAxisGraphWidth}
                                YGrid={false}
                                padding={[0, 0, 0, 0]}
                                yAxisLabelFormatter={(val: any) => {
                                    return formatNumWithKMBAlt(
                                        val,
                                        showPercentageChange ? decimalForPercentage : decimalForNonPercentage,
                                        showPercentageChange ? decimalForPercentage : decimalForNonPercentage,
                                    );
                                }}
                                xAxisConfig={false}
                            />
                        </div>
                        {/* MAIN AXIS */}
                        <HorizontalInfiniteScroller
                            id={`category-movement-${isTopChart ? 'left' : 'right'}-infinite-scroll${chartItem.id}`}
                            hasMore={chartItem?.data?.length < (chartItem.count ?? 0) || false}
                            scrollThreshold={0.9}
                            toScrollWidth={chartItem?.width}
                            onNext={(width: number) => {}}
                        >
                            <div
                                key={chartItem?.id}
                                id={`category-movement-${isTopChart ? 'left' : 'right'}-infinite-scroll${chartItem.id}`}
                                className="flex pb-[45px]"
                                style={{ maxWidth: '100%', overflowX: 'auto' }}
                            >
                                {chartItem &&
                                    chartItem?.data?.length > 0 &&
                                    chartItem?.data?.map((item: any, index: number) => {
                                        const isSelected = Boolean(selectedChartItems?.[idx] === item?.featureName);
                                        return (
                                            <div key={item.id || item.featureName || index}>
                                                <div
                                                    onClick={() => {
                                                        const tempArr = [...selectedChartItems];
                                                        tempArr.splice(idx, 1, item?.featureName);
                                                        setSelectedChartItems(tempArr);
                                                    }}
                                                    className={`${isSelected ? 'bg-bgBackground ' : ''} cursor-pointer`}
                                                    style={{ height: height }}
                                                >
                                                    <Barchart
                                                        barWidth={barWidth}
                                                        dataset={[item]}
                                                        height={height}
                                                        loading={false}
                                                        chartContainer={`${
                                                            isTopChart ? 'left' : 'right'
                                                        }-SalesX-${index}-${chartItem.id}`}
                                                        fields={['category', 'sales']}
                                                        intervalLabelTooltipKey={'labelSales'}
                                                        minBound={scaleMinValue(80, chartItem?.minVal)}
                                                        maxBound={scaleMaxValue(80, chartItem?.maxVal)}
                                                        width={perGraphComponentWidth || salesGraphWidth}
                                                        tooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: xAxis,
                                                                value: `${
                                                                    idx === 0 || showPercentageChange ? '' : currency
                                                                }${formatNumWithKMBAlt(
                                                                    val,
                                                                    showPercentageChange
                                                                        ? decimalForPercentage
                                                                        : decimalForNonPercentage,
                                                                    showPercentageChange
                                                                        ? decimalForPercentage
                                                                        : decimalForNonPercentage,
                                                                )}${showPercentageChange ? '%' : ''}`,
                                                            };
                                                        }}
                                                        intervalFormatter={(val) => {
                                                            return `${
                                                                idx === 0 || showPercentageChange ? '' : currency
                                                            }${formatNumWithKMBAlt(
                                                                val,
                                                                showPercentageChange
                                                                    ? decimalForPercentage
                                                                    : decimalForNonPercentage,
                                                                showPercentageChange
                                                                    ? decimalForPercentage
                                                                    : decimalForNonPercentage,
                                                            )}${showPercentageChange ? '%' : ''}`;
                                                        }}
                                                        colorKey={'color'}
                                                    />
                                                </div>
                                                {/* X AXIS ITEM LABEL */}
                                                <div className="flex items-center">
                                                    <GraphXAxisLabelItem
                                                        width={perGraphComponentWidth || salesGraphWidth}
                                                        item={item}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </HorizontalInfiniteScroller>
                    </div>
                    <p className="text-lightBlack font-xs relative top-[-40px] left-[25px] flex justify-center">
                        {chartItem.labelObject?.xAxis}
                    </p>
                </div>
            )}
        </div>
    );
};

export default CategoryMovementChartItem;
